import React, { useEffect, useRef, useState } from "react";
import { Loader2, RotateCcw, Search, Square, SquareCheckBig } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import api from "~/services/api";
import { formatter } from "~/functions";
import ModalShell from "./ModalShell";
import {
  theme,
  field,
  primaryButton,
  ghostButton,
  label,
  spread as row,
} from "./modalStyles";

function safeDate(value) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM/dd/yyyy");
  } catch (err) {
    return "-";
  }
}

export default function SettlementsModal({ item, onClose, onConciliated }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("byDate");
  const [lists, setLists] = useState({ byDate: [], byAmount: [], bySearch: [] });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [memo, setMemo] = useState("");
  const [askMemo, setAskMemo] = useState(false);
  const [currentItem, setCurrentItem] = useState(item);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const requestId = useRef(0);

  async function loadSettlements(searchValue = search, from = dateFrom, to = dateTo) {
    if (from && to && from > to) {
      toast("The start date must be before the end date.", { type: "warning" });
      return;
    }
    const currentRequest = ++requestId.current;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/gravityconciliations-items/${item.id}/settlements`,
        { params: { search: searchValue, date_from: from, date_to: to } }
      );
      if (currentRequest !== requestId.current) return;
      if (data.item) setCurrentItem(data.item);
      setLists({
        byDate: data.byDate || [],
        byAmount: data.byAmount || [],
        bySearch: data.bySearch || [],
      });
    } catch (err) {
      if (currentRequest !== requestId.current) return;
      setLists({ byDate: [], byAmount: [], bySearch: [] });
      toast(err?.response?.data?.error || "Could not load settlements.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }

  useEffect(() => {
    setCurrentItem(item);
    setSelected([]);
    setMemo("");
    setAskMemo(false);
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setTab("byDate");
    loadSettlements("", "", "");
    return () => { requestId.current += 1; };
  }, [item.id]);

  function handleSearch(event) {
    event.preventDefault();
    setTab("bySearch");
    loadSettlements(search);
  }

  function toggle(settlement) {
    setAskMemo(false);
    setSelected((prev) =>
      prev.find((el) => el.id === settlement.id)
        ? prev.filter((el) => el.id !== settlement.id)
        : [...prev, settlement]
    );
  }

  const itemAmount = Number(currentItem.amount || 0);
  const isRefund = itemAmount < 0;
  const selectedTotal = selected.reduce(
    (total, el) => total + (isRefund ? Math.abs(Number(el.amount || 0)) : Number(el.amount || 0)),
    0
  );
  const selectedAmount = isRefund && selectedTotal ? -selectedTotal : selectedTotal;
  const difference = itemAmount - selectedAmount;
  const isZero = Math.abs(difference) < 0.005;

  async function handleConfirm() {
    if (!selected.length) {
      toast("Select at least one settlement.", {
        type: "warning",
        autoClose: 2000,
      });
      return;
    }
    if (!isZero && !memo.trim()) {
      setAskMemo(true);
      toast("A justification is required when the amounts differ.", {
        type: "warning",
        autoClose: 3000,
      });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/gravityconciliations-items/${item.id}/settlements`, {
        [isRefund ? "payeesettlement_ids" : "settlement_ids"]: selected.map((el) => el.id),
        ...(memo.trim() ? { memo: memo.trim() } : {}),
      });
      toast("Item conciliated!", { autoClose: 1000 });
      onConciliated();
      onClose();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.difference !== undefined) {
        setAskMemo(true);
        toast(
          `Difference of ${formatter.format(
            data.difference
          )} — a justification is required.`,
          { type: "error", autoClose: 4000 }
        );
      } else {
        toast(data?.error || "Could not conciliate this item.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } finally {
      setSaving(false);
    }
  }

  const rows = lists[tab] || [];

  const tabs = [
    { name: "byDate", title: "By date" },
    { name: "byAmount", title: "By amount" },
    { name: "bySearch", title: "Search" },
  ];

  return (
    <ModalShell title="Conciliate item" onClose={onClose} draggable>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 16px",
          borderBottom: `1px solid ${theme.border}`,
          color: theme.text,
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        <strong>{currentItem.name || "(no name)"}</strong>
        <span>·</span>
        <span>{safeDate(currentItem.settled_on)}</span>
        <span>·</span>
        <strong>{formatter.format(itemAmount)}</strong>
        <span>·</span>
        <span>Ref: {currentItem.reference || "-"}</span>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            loadSettlements();
          }}
          title="Settlement date range. Without a range, By date uses the transaction date; By amount and Search use all dates."
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 4,
            marginLeft: "auto",
          }}
        >
          <input
            type="date"
            aria-label="Settlement date from"
            title="Settlement date from"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            style={{ ...field, width: 120, height: 26, padding: "3px 6px", fontSize: 11, boxSizing: "border-box" }}
          />
          <span style={{ ...label, fontSize: 11 }}>to</span>
          <input
            type="date"
            aria-label="Settlement date to"
            title="Settlement date to"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            style={{ ...field, width: 120, height: 26, padding: "3px 6px", fontSize: 11, boxSizing: "border-box" }}
          />
          <button
            type="submit"
            disabled={loading}
            style={{ ...primaryButton, height: 26, padding: "3px 8px", fontSize: 11 }}
          >
            Apply
          </button>
          <button
            type="button"
            aria-label="Clear date range"
            title="Clear date range"
            disabled={loading}
            style={{ ...ghostButton, width: 26, height: 26, padding: 4, display: "flex", alignItems: "center", justifyContent: "center" }}
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              loadSettlements(search, "", "");
            }}
          >
            <RotateCcw size={13} />
          </button>
        </form>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "16px 16px 0 16px",
          flexShrink: 0,
        }}
      >
        {tabs.map(({ name, title }) => (
          <button
            key={name}
            type="button"
            onClick={() => setTab(name)}
            style={{
              ...(tab === name ? primaryButton : ghostButton),
              padding: "5px 14px",
            }}
          >
            {title}
          </button>
        ))}
      </div>

      {tab === "bySearch" ? (
        <form
          onSubmit={handleSearch}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px 0 16px",
            flexShrink: 0,
          }}
        >
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Student name, invoice number..."
            style={{ ...field, flex: 1, width: "auto" }}
          />
          <button type="submit" style={primaryButton}>
            <Search size={14} /> Search
          </button>
        </form>
      ) : null}

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16 }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "32px 0",
              color: theme.muted,
            }}
          >
            <Loader2 size={16} className="animate-spin" /> Loading...
          </div>
        ) : rows.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: theme.muted,
              padding: "32px 0",
            }}
          >
            No settlements found.
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              borderCollapse: "collapse",
            }}
          >
            <tbody>
              {rows.map((settlement) => {
                const checked = !!selected.find((el) => el.id === settlement.id);
                return (
                  <tr
                    key={settlement.id}
                    onClick={() => toggle(settlement)}
                    style={{
                      cursor: "pointer",
                      borderBottom: `1px solid ${theme.border}`,
                      backgroundColor: checked
                        ? theme.successBg
                        : "transparent",
                    }}
                  >
                    <td style={{ padding: 8, width: 32 }}>
                      {checked ? (
                        <SquareCheckBig size={16} color={theme.success} />
                      ) : (
                        <Square size={16} color={theme.border} />
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      {settlement.student?.name ||
                        settlement.receivable?.issuer?.name ||
                        settlement.payee?.issuer?.name ||
                        settlement.name ||
                        "-"}
                    </td>
                    <td style={{ padding: 8, color: theme.muted }}>
                      {settlement.invoice_number ||
                        settlement.receivable?.invoice_number ||
                        settlement.payee?.invoice_number ||
                        "-"}
                    </td>
                    <td style={{ padding: 8, color: theme.muted }}>
                      {safeDate(settlement.settlement_date)}
                    </td>
                    <td
                      style={{
                        padding: 8,
                        textAlign: "right",
                        fontWeight: "bold",
                      }}
                    >
                      {formatter.format(isRefund
                        ? -Math.abs(Number(settlement.amount || 0))
                        : Number(settlement.amount || 0))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div
        style={{
          borderTop: `1px solid ${theme.border}`,
          backgroundColor: theme.subtle,
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <div style={row}>
          <span style={label}>Selected</span>
          <strong>{formatter.format(selectedAmount)}</strong>
        </div>
        <div style={row}>
          <span style={label}>Item</span>
          <strong>{formatter.format(itemAmount)}</strong>
        </div>
        <div style={row}>
          <span style={label}>Difference</span>
          <strong style={{ color: isZero ? theme.success : theme.danger }}>
            {formatter.format(difference)}
          </strong>
        </div>

        {askMemo || (!isZero && selected.length > 0) ? (
          <textarea
            rows={2}
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
            placeholder="Justification for the difference (required)"
            style={field}
          />
        ) : null}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            paddingTop: 8,
          }}
        >
          <button type="button" onClick={onClose} style={ghostButton}>
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || loading}
            onClick={handleConfirm}
            style={{ ...primaryButton, opacity: saving ? 0.5 : 1 }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Confirm
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
